import type { ChangeEvent, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import cc from 'classcat'

import {
  CATEGORY_PAGES,
  CATEGORY_PRODUCTS,
  SET_ERROR_STATE,
  SET_IS_EDITING,
  SET_LINK_DETAILS,
} from '../LinkTooltip.constants'
import type { ItemType } from '../LinkTooltip.utils'
import { Popover } from '../../../../../../Popover'
import { formatHref } from '../../../../utils/link-sanitizer'
import { useTooltipState } from '../LinkTooltipContext'
import { useTranslate } from '../../../../../../../utils/translate'
import LinkTooltipSearchResultsDropdown from './SearchResultsDropdown'

interface EditModeProps {
  readonly inputRef?: RefObject<HTMLInputElement | null>
  readonly handleInsert: () => void
  readonly isSettingsLayer?: boolean
  readonly handleClickOutside?: () => void
  readonly disabled?: boolean
}

export default function EditMode({
  inputRef: propInputRef,
  handleInsert,
  isSettingsLayer,
  handleClickOutside,
  disabled,
}: EditModeProps) {
  const { categoryState, linkTooltipState, pages, products, errorState } = useTooltipState()
  const { inputValue, url, searchTerm, isEditing } = linkTooltipState
  const defaultInputRef = useRef<HTMLInputElement>(null)
  const inputRef = propInputRef || defaultInputRef

  const [showPopover, setShowPopover] = useState(false)

  const {
    handleItemClick,
    handleInputChange,
    handleInputFocus,
    handleClearButtonClick,
    handleInsertClick,
    handleAddLinkOnEnterKeyPress,
  } = useEditModeActions(inputRef, handleInsert)

  const t = useTranslate('interface', 'components.richTextComponent')

  const categories = [
    { name: CATEGORY_PAGES, items: pages },
    { name: CATEGORY_PRODUCTS, items: products },
  ]

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef, isEditing])

  useEffect(() => {
    setShowPopover(isEditing)
  }, [isEditing])

  useEffect(() => {
    // In case of error, focus input field
    if (errorState.hasError && inputRef.current) {
      inputRef.current.focus()
    }
  }, [errorState.hasError, inputRef])

  return (
    <>
      <input
        type="text"
        className={cc([
          'dali-plugin-link-tooltip-input',
          { 'dali-plugin-link-tooltip-input-border-right': !isSettingsLayer || isEditing },
        ])}
        data-testid="link-tooltip-edit-mode"
        placeholder={!isSettingsLayer || isEditing ? getPlaceholderText(categoryState.selectedCategory, t) : undefined}
        ref={inputRef}
        value={inputValue}
        title={url}
        aria-label={t('linkInputField.ariaLabel')}
        onChange={handleInputChange}
        onKeyDown={handleAddLinkOnEnterKeyPress}
        onFocus={handleInputFocus}
        disabled={disabled}
      />
      {!isSettingsLayer || isEditing ? (
        <>
          {inputValue && (
            <button
              type="button"
              className="dali-plugin-link-tooltip-clear-button"
              data-testid="link-tooltip-edit-mode-clear-button"
              aria-label={t('linkInputField.clearButton.ariaLabel')}
              onClick={handleClearButtonClick}
            ></button>
          )}
          <button
            type="button"
            aria-label={t('linkInputField.insertButton.ariaLabel')}
            onClick={(e) => {
              e.preventDefault()
              handleInsertClick()
            }}
          >
            <span className="dali-plugin-text-tooltip-check" />
          </button>
          {isSettingsLayer ? (
            <Popover
              open={showPopover}
              virtualRef={inputRef}
              className="dali-plugin-link-tooltip-popover"
              sideOffset={1}
              align="start"
              onPointerDownOutside={(event) => {
                const target = event.target as HTMLElement
                if (
                  target.matches('.dali-plugin-link-tooltip-input') ||
                  target.matches('.dali-plugin-link-tooltip-clear-button') ||
                  target.matches('.dali-plugin-text-tooltip-check')
                ) {
                  return
                }

                if (handleClickOutside) handleClickOutside()
              }}
              hideArrow
            >
              <>
                {errorState.hasError ? (
                  <div className="dali-plugin-link-tooltip-error-message">
                    <span>{t('linkInputField.invalidUrlError')}</span>
                  </div>
                ) : null}
                <LinkTooltipSearchResultsDropdown
                  inputRef={inputRef}
                  categories={categories}
                  onItemClick={handleItemClick}
                  searchTerm={searchTerm}
                  isSettingsLayer={isSettingsLayer}
                />
              </>
            </Popover>
          ) : (
            <>
              {errorState.hasError ? (
                <div className="dali-plugin-link-tooltip-error-message">
                  <span>{t('linkInputField.invalidUrlError')}</span>
                </div>
              ) : null}
              <LinkTooltipSearchResultsDropdown
                inputRef={inputRef}
                categories={categories}
                onItemClick={handleItemClick}
                searchTerm={searchTerm}
                isSettingsLayer={isSettingsLayer}
              />
            </>
          )}
        </>
      ) : null}
    </>
  )
}

function getPlaceholderText(selectedCategory: string | null, t: (key: string) => string): string {
  if (selectedCategory === CATEGORY_PAGES) {
    return t('linkInputField.placeholder.pages')
  } else if (selectedCategory === CATEGORY_PRODUCTS) {
    return t('linkInputField.placeholder.products')
  } else {
    return t('linkInputField.placeholder.default')
  }
}

function useEditModeActions(inputRef: RefObject<HTMLInputElement | null>, handleInsert: () => void) {
  const { linkTooltipState, linkTooltipDispatch, errorState, errorDispatch } = useTooltipState()

  function handleItemClick(item: ItemType) {
    if (errorState.hasError) {
      errorDispatch({ type: SET_ERROR_STATE, payload: { hasError: false } })
    }

    linkTooltipDispatch({
      type: SET_LINK_DETAILS,
      payload: {
        inputValue: item.title,
        url: item.link,
        title: '',
        type: item.type,
      },
    })
    inputRef?.current?.focus()
  }

  function dispatchLinkTooltipActions(value: string) {
    linkTooltipDispatch({
      type: SET_LINK_DETAILS,
      payload: {
        inputValue: value,
        url: value,
        searchTerm: value,
      },
    })

    if (errorState.hasError) {
      errorDispatch({ type: SET_ERROR_STATE, payload: { hasError: false } })
    }
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement
    dispatchLinkTooltipActions(target.value)
  }

  function handleInputFocus() {
    linkTooltipDispatch({ type: SET_IS_EDITING, payload: true })
  }

  function handleClearButtonClick() {
    dispatchLinkTooltipActions('')

    inputRef?.current?.focus()
  }

  function handleInsertClick() {
    linkTooltipDispatch(formatHref(linkTooltipState.url))
    handleInsert()
  }

  function handleAddLinkOnEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleInsertClick()
    }
  }

  return {
    handleItemClick,
    handleInputChange,
    handleInputFocus,
    handleClearButtonClick,
    handleInsertClick,
    handleAddLinkOnEnterKeyPress,
  }
}
